import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, HostBinding } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { Chart, ChartEvent, ChartType, Interaction, InteractionItem, InteractionModeFunction, registerables } from 'chart.js';
import { color as chartColor, getRelativePosition } from 'chart.js/helpers';
import 'chartjs-adapter-luxon';
import { roundToPrecision } from '@cf-shared/shared-util';
import { GlobalMeasurementTypes } from '@cf-shared/measure-configs';
import { SettingsService } from 'services/settings.service';
import { verticalCrosshairPlugin } from 'utils/chart-plugins';
import { calculateTrailingAverage, calculateMinMax, shouldShowMarker } from 'utils/chart-helpers';
import { TrendDirection } from '@cf-shared/running-trend';
import { buildTrendDatasets, pickSeriesElements, ProgressChartTrend, TREND_DIRECTION_COLOR_VARS, TREND_TUNNEL_FILL_ALPHA, TrendDirectionColors, trendValuesForSeries } from './progress-chart-trend';
import { SeriesUnit, seriesUnit } from './progress-chart-units';

export type { ProgressChartTrend } from './progress-chart-trend';

// Register Chart.js components
Chart.register(...registerables);

declare module 'chart.js' {
  interface InteractionModeMap {
    seriesIndex: InteractionModeFunction;
  }
}

/**
 * The hover mode of this chart: the nearest weigh-in across the series datasets only, never a
 * running trend dataset (#6627). Chart.js's 'index' mode would match trend points by position,
 * which is a different date. The choice itself is `pickSeriesElements`, unit-tested.
 */
Interaction.modes.seriesIndex = (chart: Chart, e: ChartEvent, _options: unknown, useFinalPosition?: boolean): InteractionItem[] => {
  const position = getRelativePosition(e, chart);
  // like chart.js's own 'index' mode, a pointer outside the plotting area picks nothing
  if (!chart.isPointInArea(position)) return [];
  const datasets = chart.data.datasets.map((dataset, datasetIndex) => ({
    datasetIndex,
    isTrend: !!(dataset as { isTrend?: boolean }).isTrend,
    hidden: !chart.isDatasetVisible(datasetIndex),
    elements: chart.getDatasetMeta(datasetIndex).data.map(element => ({
      x: element.getProps(['x'], useFinalPosition).x as number,
      skip: (element as { skip?: boolean }).skip,
    })),
  }));
  return pickSeriesElements(datasets, position.x).map(item => ({
    ...item,
    element: chart.getDatasetMeta(item.datasetIndex).data[item.index],
  }));
};

/**
 * Series configuration for the progress chart.
 * Defines how each data series should be displayed.
 */
export interface ProgressChartSeries {
  id: string;                       // unique identifier (matches data key)
  label: string;                    // translated text to show (legend, tooltip)
  colorVar: string;                 // CSS variable name (e.g., '--chart-purple')
  yAxisId?: string;                 // for stacked axes (e.g., 'y-3-weight')
  yAxisLabel?: string;              // translated axis title, unit already included (from seriesUnit) where needed
  dashed?: boolean;                 // dashed line style
  hidden?: boolean;                 // controlled by filter toggle

  // Unit conversion config (chart handles conversion; data always arrives in canonical units)
  // References GlobalMeasurementTypes id from measure-configs.ts
  measurementTypeId?: string;       // e.g., 'bodyweight_kg', 'bodydimensions_cm', 'nutrient_kcal'
  // unit config id to show this series in, instead of the person's stored preference for the type,
  // e.g. the exercise's own sticky ExerciseStat.weight_unit (#3297). Resolved by seriesUnit()
  displayUnit?: string;
  precision?: number;               // decimal places for display (overrides measurementType default)

  // Tooltip extra field - show additional data in tooltip (e.g., reps for weight)
  tooltipExtraField?: string;       // data field name to display (e.g., 'reps')
  tooltipExtraLabel?: string;       // label for extra field (e.g., 'Reps')
}

/**
 * Data point format for the progress chart.
 * Each point has a date and values for each series.
 */
export interface ProgressChartDataPoint {
  date: string;                     // ISO date (YYYY-MM-DD)
  [seriesId: string]: string | number | undefined;  // canonical units (kg, cm, mm, kcal)
}

/**
 * Axis configuration for the progress chart.
 */
export interface ProgressChartAxisConfig {
  stacked: boolean;                 // multiple stacked Y-axes
  yAxisTitle?: string;              // single axis title (for non-stacked), already translated
  yAxisUnit?: string;               // unit suffix (e.g., 'kcal')
}

@Component({
  selector: 'cs-progress-chart',
  templateUrl: 'cs-progress-chart.html',
  host: { 'class': 'progress-chart' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsProgressChart implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('chartCanvas', { static: false }) chartCanvas!: ElementRef<HTMLCanvasElement>;

  // Display mode
  @Input() compact: boolean = false;
  @HostBinding('attr.compact')
  get compactAttr() {
    return this.compact ? '' : null;
  }

  // Dynamic chart height for stacked axes
  @HostBinding('style.--chart-height')
  get chartHeight() {
    if (!this.axisConfig.stacked) {
      return '350px';
    }
    const visibleCount = this.visibleSeries.length;
    // minimum height should be for 2 charts, maximum for 3 charts
    const effectiveCount = Math.max(2, visibleCount);
    const height = 350 * (effectiveCount / 3);
    return `${height}px`;
  }

  // Common inputs
  @Input() showLegend: boolean = true;
  @Input() timePeriod: 'week' | 'month' | 'quarter' | 'year' | 'all' | '2weeks' | '4weeks' | '8weeks' | '12weeks' = 'month';
  @Input() startDate: string = '';
  @Input() endDate: string = '';
  @Input() showAvg: boolean = false;
  @Input() showPoints: boolean = false;

  // Series and data configuration
  @Input() series: ProgressChartSeries[] = [];
  @Input() data: ProgressChartDataPoint[] = [];
  @Input() axisConfig: ProgressChartAxisConfig = { stacked: false };
  /** the running trend of some series (#6627); empty draws exactly the chart as it was before */
  @Input() trends: ProgressChartTrend[] = [];

  @Output() chartClick = new EventEmitter<void>();

  // Loading state - controlled by parent
  @Input() isLoading: boolean = false;

  private chart?: Chart;
  private colorsInitialized: boolean = false;

  // Legend items derived from series
  legendItems: { color: string; label: string }[] = [];

  constructor(
    private cdRef: ChangeDetectorRef,
    private el: ElementRef,
    private settings: SettingsService,
  ) {}

  // Get visible (non-hidden) series
  get visibleSeries(): ProgressChartSeries[] {
    return this.series.filter(s => !s.hidden);
  }

  // Get filtered legend items based on visible series
  get filteredLegendItems() {
    return this.legendItems.filter((_, index) => {
      const seriesItem = this.series[index];
      return seriesItem && !seriesItem.hidden;
    });
  }

  /** the direction legend shows wherever a coloured tunnel is drawn, never on the compact chart */
  get showTrendLegend(): boolean {
    if (this.compact) return false;
    const visibleIds = new Set(this.visibleSeries.map(s => s.id));
    return (this.trends || []).some(t => t.showTunnel && visibleIds.has(t.seriesId) && t.rows.some(r => r.value !== undefined));
  }

  ngOnChanges(changes: SimpleChanges) {
    // if any input changes and we have a chart, update it
    if (this.chart && (changes['data'] || changes['series'] || changes['timePeriod'] ||
        changes['showAvg'] || changes['showPoints'] || changes['axisConfig'] || changes['trends'])) {
      this.updateChart();
    }
  }

  ngAfterViewInit() {
    this.initializeColors();
    this.createChart();
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  private initializeColors() {
    const styles = getComputedStyle(document.documentElement);

    // Build legend items from series (with translated labels)
    this.legendItems = this.series.map(s => ({
      color: styles.getPropertyValue(s.colorVar).trim(),
      label: s.label
    }));

    this.colorsInitialized = true;
    this.cdRef.markForCheck();
  }

  private getXAxisTimeConfig(): { unit: string; format: string } {
    switch (this.timePeriod) {
      case 'week':
        return { unit: 'day', format: 'EEE' }; // Mon, Tue, Wed
      case 'month':
      case '2weeks':
      case '4weeks':
        return { unit: 'day', format: 'd' }; // 1, 5, 10
      case 'quarter':
      case '8weeks':
      case '12weeks':
        return { unit: 'month', format: 'MMM' }; // Sep, Oct, Nov
      case 'year':
        return { unit: 'month', format: 'MMM yy' }; // Sep 24, Jan 25
      case 'all':
        return { unit: 'month', format: 'MMM yy' }; // Sep 24, Jan 25
      default:
        return { unit: 'month', format: 'MMM' };
    }
  }

  private getXAxisTitle(): string {
    switch (this.timePeriod) {
      case 'week':
        return LL.progress.axisTitles.day();
      case 'month':
      case '2weeks':
      case '4weeks':
        return LL.progress.axisTitles.day();
      default:
        return LL.progress.axisTitles.month();
    }
  }

  private getMeasurementPrecision(measurementTypeId: string, overridePrecision?: number): number {
    // if override specified, use it (but ensure it's not 0 which breaks roundToPrecision)
    if (overridePrecision !== undefined && overridePrecision !== 0) return overridePrecision;
    const measurementType = GlobalMeasurementTypes.find(mt => mt.id === measurementTypeId);
    // default to 1 (whole numbers) if no precision found
    return measurementType?.precision ?? 1;
  }

  /** a trend value in canonical units, converted the same way as its series */
  private convertTrendValue = (seriesId: string, value: number): number => {
    const series = this.visibleSeries.find(s => s.id === seriesId);
    return (series ? this.convertValue(value, series) : undefined) ?? value;
  };

  /** the 4 direction tints, read from the theme */
  private readDirectionColors(): TrendDirectionColors {
    const styles = getComputedStyle(document.documentElement);
    const color = (direction: TrendDirection) => styles.getPropertyValue(TREND_DIRECTION_COLOR_VARS[direction]).trim();
    return {
      [TrendDirection.Falling]: color(TrendDirection.Falling),
      [TrendDirection.Rising]: color(TrendDirection.Rising),
      [TrendDirection.Flat]: color(TrendDirection.Flat),
      [TrendDirection.Uncertain]: color(TrendDirection.Uncertain),
    };
  }

  /** the unit one series is shown in; the chart-data services ask the same helper for the axis unit name */
  private getSeriesUnit(series: ProgressChartSeries): SeriesUnit {
    return seriesUnit(series.measurementTypeId, series.displayUnit, this.settings.locale, type => this.settings.getUnitPref(type));
  }

  private convertValue(value: number | undefined, series: ProgressChartSeries): number | undefined {
    if (value === undefined) return undefined;
    return value * this.getSeriesUnit(series).factor;
  }

  private getAxisTitle(series: ProgressChartSeries): string {
    // the caller's text already carries the unit, built from the same seriesUnit() answer
    return series.yAxisLabel ?? '';
  }

  private processData(): { labels: string[]; seriesData: Map<string, (number | undefined)[]> } {
    const labels = this.data.map(p => p.date);
    const seriesData = new Map<string, (number | undefined)[]>();

    for (const s of this.visibleSeries) {
      const precision = this.getMeasurementPrecision(s.measurementTypeId || '', s.precision);

      let values = this.data.map(p => {
        const raw = p[s.id];
        if (typeof raw !== 'number') return undefined;
        const converted = this.convertValue(raw, s);
        return converted !== undefined ? roundToPrecision(converted, precision) : undefined;
      });

      // Apply trailing average if enabled
      if (this.showAvg) {
        values = calculateTrailingAverage(values, precision);
      }

      seriesData.set(s.id, values);
    }

    return { labels, seriesData };
  }

  private buildDatasets(seriesData: Map<string, (number | undefined)[]>) {
    const rootStyles = getComputedStyle(document.documentElement);
    const chartPointBackgroundColor = rootStyles.getPropertyValue('--chart-point-background').trim();
    const chartPointRadius = parseFloat(rootStyles.getPropertyValue('--chart-point-radius'));
    const chartPointHoverRadius = parseFloat(rootStyles.getPropertyValue('--chart-point-hover-radius'));
    const chartPointBorderWidth = parseFloat(rootStyles.getPropertyValue('--chart-point-border-width'));

    const datasets: any[] = [];

    for (const s of this.visibleSeries) {
      const data = seriesData.get(s.id) || [];
      const color = rootStyles.getPropertyValue(s.colorVar).trim();

      // a single-point series cannot draw a line, so it must render a
      // marker or the lone value is invisible — see shouldShowMarker
      // and #3704.
      const showMarker = shouldShowMarker(data, this.showPoints);

      datasets.push({
        label: s.label,
        data,
        borderColor: color,
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: s.dashed ? [5, 5] : undefined,
        tension: this.axisConfig.stacked ? 0.1 : 0,
        pointRadius: showMarker ? chartPointRadius : 0,
        hoverBorderWidth: chartPointBorderWidth,
        hoverRadius: !this.compact ? chartPointHoverRadius : 0,
        pointBackgroundColor: chartPointBackgroundColor,
        pointBorderWidth: chartPointBorderWidth,
        pointBorderColor: color,
        yAxisID: s.yAxisId || 'y',
        spanGaps: true,
        seriesId: s.id,
      });
    }

    // the running trend layers go after the series, so chart.js draws them underneath
    datasets.push(...buildTrendDatasets({
      trends: this.trends || [],
      visibleSeries: this.visibleSeries.map(s => ({ id: s.id, yAxisId: s.yAxisId, color: rootStyles.getPropertyValue(s.colorVar).trim() })),
      convert: this.convertTrendValue,
      directionColors: this.readDirectionColors(),
      tunnelFill: tint => chartColor(tint).alpha(TREND_TUNNEL_FILL_ALPHA).rgbString(),
    }));

    return datasets;
  }

  private buildScales(seriesData: Map<string, (number | undefined)[]>, tickColor: string, gridColor: string, fontSize: number, fontFamily: string) {
    const timeConfig = this.getXAxisTimeConfig();

    const scales: any = {
      x: {
        type: 'time',
        time: {
          unit: timeConfig.unit as any,
          displayFormats: {
            [timeConfig.unit]: timeConfig.format
          },
          tooltipFormat: 'yyyy-MM-dd'
        },
        grid: { display: false },
        ticks: {
          color: tickColor,
          font: { size: fontSize, family: fontFamily },
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 5
        },
        border: { display: false },
        title: {
          display: !this.compact,
          text: this.getXAxisTitle(),
          color: tickColor,
          font: { size: fontSize, family: fontFamily },
          padding: { top: 8 }
        }
      }
    };

    if (this.axisConfig.stacked) {
      // Build stacked Y-axes for each visible series
      this.buildStackedYAxes(scales, seriesData, tickColor, gridColor, fontSize, fontFamily);
    } else {
      // Build single Y-axis
      this.buildSingleYAxis(scales, seriesData, tickColor, gridColor, fontSize, fontFamily);
    }

    return scales;
  }

  private buildStackedYAxes(
    scales: any,
    seriesData: Map<string, (number | undefined)[]>,
    tickColor: string,
    gridColor: string,
    fontSize: number,
    fontFamily: string
  ) {
    const visibleCount = this.visibleSeries.length;
    const TOTAL_TICKS = 9;
    const tickCount = visibleCount > 0 ? Math.floor(TOTAL_TICKS / visibleCount) : 3;
    const shouldHideTopLabel = visibleCount > 1;

    // Sort by yAxisId to determine stacking order
    const sortedSeries = [...this.visibleSeries].sort((a, b) =>
      (a.yAxisId || 'y').localeCompare(b.yAxisId || 'y')
    );

    sortedSeries.forEach((s, index) => {
      const data = seriesData.get(s.id) || [];
      // the panel holds the trend's tunnel too, so it is never clipped
      const minMax = calculateMinMax([...data, ...trendValuesForSeries(this.trends || [], s.id, this.convertTrendValue)]);
      const precision = this.getMeasurementPrecision(s.measurementTypeId || '', s.precision);
      const maxTickIndex = tickCount - 1;
      const isTopmost = index === sortedSeries.length - 1;

      scales[s.yAxisId || 'y'] = {
        type: 'linear',
        position: 'left',
        stack: 'charts',
        stackWeight: 1,
        min: minMax.min,
        max: minMax.max,
        grid: {
          color: gridColor,
          lineWidth: 1
        },
        ticks: {
          display: !this.compact,
          count: tickCount,
          color: tickColor,
          font: { size: fontSize, family: fontFamily },
          callback: (value: any, tickIndex: number) => {
            // hide top label if not topmost and there are multiple charts
            if (shouldHideTopLabel && !isTopmost && tickIndex === maxTickIndex) {
              return '';
            }
            return roundToPrecision(value, precision);
          }
        },
        border: { display: false },
        title: {
          display: !this.compact,
          text: this.getAxisTitle(s),
          color: tickColor,
          font: { size: fontSize, family: fontFamily }
        }
      };
    });
  }

  private buildSingleYAxis(
    scales: any,
    seriesData: Map<string, (number | undefined)[]>,
    tickColor: string,
    gridColor: string,
    fontSize: number,
    fontFamily: string
  ) {
    // Combine all visible series data for single axis range
    const allValues: (number | undefined)[] = [];
    for (const s of this.visibleSeries) {
      const data = seriesData.get(s.id) || [];
      allValues.push(...data, ...trendValuesForSeries(this.trends || [], s.id, this.convertTrendValue));
    }

    const minMax = calculateMinMax(allValues);

    // Get precision from first series or default to 1 (whole numbers)
    const firstSeries = this.visibleSeries[0];
    const precision = firstSeries
      ? this.getMeasurementPrecision(firstSeries.measurementTypeId || '', firstSeries.precision)
      : 1;

    // Build Y-axis title
    let yAxisTitle = this.axisConfig.yAxisTitle || '';
    if (this.axisConfig.yAxisUnit) {
      yAxisTitle = `${yAxisTitle} (${this.axisConfig.yAxisUnit})`;
    }

    scales['y'] = {
      type: 'linear',
      position: 'left',
      min: minMax.min,
      max: minMax.max,
      grid: {
        color: gridColor,
        lineWidth: 1
      },
      ticks: {
        display: !this.compact,
        count: 3,
        color: tickColor,
        font: { size: fontSize, family: fontFamily },
        callback: (value: any) => roundToPrecision(value, precision)
      },
      border: { display: false },
      title: {
        display: !this.compact,
        text: yAxisTitle,
        color: tickColor,
        font: { size: fontSize, family: fontFamily }
      }
    };
  }

  private createChart() {
    if (!this.chartCanvas?.nativeElement) return;
    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) return;

    // Process data and build chart config
    const { labels, seriesData } = this.processData();
    const datasets = this.buildDatasets(seriesData);

    // Get colors from CSS
    const rootStyles = getComputedStyle(document.documentElement);
    const tooltipBg = rootStyles.getPropertyValue('--chart-tooltip-bg').trim();
    const tooltipTitle = rootStyles.getPropertyValue('--chart-tooltip-title').trim();
    const tooltipBody = rootStyles.getPropertyValue('--chart-tooltip-body').trim();
    const tooltipBorder = rootStyles.getPropertyValue('--chart-tooltip-border').trim();
    const gridColor = rootStyles.getPropertyValue('--chart-grid').trim();
    const tickColor = rootStyles.getPropertyValue('--chart-tick').trim();

    // Get typography from component (with fallbacks to prevent Chart.js font resolution errors)
    const componentStyles = getComputedStyle(this.el.nativeElement);
    const fontSize = parseInt(componentStyles.fontSize, 10) || 12;
    const fontFamily = componentStyles.fontFamily || 'system-ui, sans-serif';

    const config = {
      type: 'line' as ChartType,
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        onClick: () => {
          if (this.compact) {
            this.chartClick.emit();
          }
        },
        plugins: {
          verticalCrosshair: { componentRef: this },
          legend: { display: false },
          tooltip: {
            enabled: !this.compact,
            backgroundColor: tooltipBg,
            titleColor: tooltipTitle,
            bodyColor: tooltipBody,
            borderColor: tooltipBorder,
            borderWidth: 1,
            padding: 12,
            displayColors: true,
            usePointStyle: true,
            boxWidth: 7,
            boxHeight: 7,
            boxPadding: 4,
            multiKeyBackground: 'transparent',
            // the running trend is context, never a tooltip line
            filter: (item: any) => !item.dataset?.isTrend,
            callbacks: {
              labelColor: function(context: any) {
                return {
                  backgroundColor: context.dataset.borderColor as string,
                  borderColor: context.dataset.borderColor as string,
                  borderWidth: 0
                };
              },
              label: (context: any) => {
                const dataIndex = context.dataIndex;
                const value = context.formattedValue;
                const label = context.dataset.label;

                // find the series config by the dataset's id, never by position: trend datasets follow the series ones
                const series = this.visibleSeries.find(s => s.id === context.dataset.seriesId);
                if (!series) return `${label}: ${value}`;

                // check for extra field
                if (series.tooltipExtraField && this.data[dataIndex]) {
                  const extraValue = this.data[dataIndex][series.tooltipExtraField];
                  if (extraValue !== undefined) {
                    const extraLabel = series.tooltipExtraLabel || series.tooltipExtraField;
                    return `${label}: ${value} (${extraLabel}: ${extraValue})`;
                  }
                }

                return `${label}: ${value}`;
              }
            }
          }
        },
        scales: this.buildScales(seriesData, tickColor, gridColor, fontSize, fontFamily),
        interaction: {
          mode: !this.compact ? 'seriesIndex' : undefined,
          intersect: false
        }
      }
    };

    // Register crosshair plugin
    Chart.register(verticalCrosshairPlugin);

    this.chart = new Chart(ctx, config as any);
  }

  private updateChart() {
    if (!this.chart) {
      this.createChart();
      return;
    }

    // Re-initialize colors if series changed
    if (!this.colorsInitialized || this.legendItems.length !== this.series.length) {
      this.initializeColors();
    }

    // Process data
    const { labels, seriesData } = this.processData();
    const datasets = this.buildDatasets(seriesData);

    // Get colors from CSS
    const rootStyles = getComputedStyle(document.documentElement);
    const gridColor = rootStyles.getPropertyValue('--chart-grid').trim();
    const tickColor = rootStyles.getPropertyValue('--chart-tick').trim();

    // Get typography from component (with fallbacks to prevent Chart.js font resolution errors)
    const componentStyles = getComputedStyle(this.el.nativeElement);
    const fontSize = parseInt(componentStyles.fontSize, 10) || 12;
    const fontFamily = componentStyles.fontFamily || 'system-ui, sans-serif';

    // Update chart data
    this.chart.data.labels = labels;
    this.chart.data.datasets = datasets;

    // Update scales
    const scales = this.buildScales(seriesData, tickColor, gridColor, fontSize, fontFamily);

    // For stacked axes, we need to recreate the chart to handle axis changes
    if (this.axisConfig.stacked) {
      this.chart.destroy();
      this.chart = undefined;
      this.createChart();
      return;
    }

    // For single axis, just update
    (this.chart.options as any).scales = scales;
    this.chart.update();
    this.cdRef.markForCheck();
  }
}
